import React from 'react';
import { motion } from 'framer-motion';
import { Rocket, Instagram, Twitter, Linkedin, Youtube } from 'lucide-react';
import { Button } from './ui/button';
import MagneticButton from './MagneticButton';

const socialLinks = [
  { icon: Instagram, label: 'Instagram', color: '#E4405F', url: 'https://www.instagram.com/sparkcampusapp' },
  { icon: Twitter, label: 'X', color: '#1DA1F2', url: 'https://x.com/sparkcampusapp' },
  { icon: Linkedin, label: 'LinkedIn', color: '#0A66C2', url: 'https://www.linkedin.com/company/sparkcampusapp' },
  { icon: Youtube, label: 'YouTube', color: '#FF0000', url: 'https://www.youtube.com/@sparkcampusapp' },
];

export const DownloadSection = () => {
  return (
    <section className="relative py-40 px-4 overflow-hidden">
      {/* Massive blurred watermark */}
      <div className="absolute inset-0 flex items-center justify-center pointer-events-none z-0">
        <motion.h1 
          className="text-[200px] md:text-[400px] font-black text-white tracking-tighter opacity-[0.02] mix-blend-overlay blur-sm select-none"
          initial={{ opacity: 0, scale: 0.8 }}
          whileInView={{ opacity: 0.02, scale: 1 }}
          transition={{ duration: 1.5, ease: "easeOut" }}
          viewport={{ once: true }}
        >
          SPARK
        </motion.h1>
      </div>

      <div className="max-w-4xl mx-auto text-center relative z-10">
        <motion.div
          className="glass-card p-12 md:p-20 rounded-[40px] relative overflow-hidden shadow-[0_0_100px_rgba(11,217,229,0.05)] border border-white/[0.05] bg-white/[0.01] backdrop-blur-[40px]"
          initial={{ opacity: 0, scale: 0.95 }}
          whileInView={{ opacity: 1, scale: 1 }}
          viewport={{ once: true }}
          transition={{ duration: 0.6 }}
        >
          {/* Background glow */}
          <div className="absolute inset-0 bg-gradient-to-br from-sp-cyan/10 to-[#06B6D4]/10" />

          <div className="relative z-10">
            <motion.div
              className="inline-block mb-6"
              animate={{ rotate: [0, 10, -10, 0] }}
              transition={{ duration: 4, repeat: Infinity, ease: 'easeInOut' }}
            >
              <Rocket className="w-16 h-16 text-sp-cyan mx-auto" />
            </motion.div>

            <motion.h2
              className="text-4xl md:text-5xl font-medium tracking-tight text-sp-text mb-4"
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ delay: 0.2, duration: 0.8 }}
            >
              Ready to Spark?
            </motion.h2>
            <p className="text-sp-text-secondary text-lg mb-8 max-w-2xl mx-auto">
              Join thousands of students making the most of their college life.
            </p>

            {/* Get Started Button */}
            <MagneticButton className="inline-block mb-8">
              <motion.a
                href="/login"
                whileHover={{ scale: 1.05 }}
                whileTap={{ scale: 0.95 }}
                className="inline-block"
              >
              <Button
                size="lg"
                className="bg-sp-cyan hover:bg-sp-cyan/80 text-black font-semibold px-12 py-6 text-lg rounded-full shadow-[0_0_40px_rgba(11,217,229,0.5)] transition-all duration-300"
              >
                Get Started
              </Button>
            </motion.a>
            </MagneticButton>

            {/* Social Links */}
            <div className="mt-8">
              <p className="text-sp-text-secondary mb-4 font-medium">Follow us for updates</p>
              <div className="flex justify-center gap-4">
                {socialLinks.map((social, index) => {
                  const Icon = social.icon;
                  return (
                    <motion.a
                      key={index}
                      href={social.url}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="glass-card p-3 rounded-full group relative"
                      whileHover={{ scale: 1.1, y: -5 }}
                      whileTap={{ scale: 0.95 }}
                      aria-label={social.label}
                    >
                      <Icon className="w-6 h-6 text-sp-text-secondary group-hover:text-sp-cyan transition-colors duration-300" />
                      
                      {/* Glow effect */}
                      <div
                        className="absolute inset-0 rounded-full opacity-0 group-hover:opacity-100 transition-opacity duration-300"
                        style={{
                          boxShadow: `0 0 20px ${social.color}50`,
                        }}
                      />
                    </motion.a>
                  );
                })}
              </div>
            </div>
          </div>
        </motion.div>
      </div>
    </section>
  );
};

export default DownloadSection;



